import React, {useCallback, useState} from 'react';

import debounce from 'lodash.debounce';
import {useEffectWithDeps} from 'src/hooks/useEffectWithDeps';
import {runAccountManagerSearch} from 'src/openapi/admin/apis/RunAccountManagerSearchApi';
import {useAppDispatch} from 'src/store/hooks';
import {isDeepEqual} from 'src/utils/isDeepEqual';

import {Autosuggest} from '@pb/kit-component-autosuggest-v2';
import Label from '@pb/kit-component-label';
import {useAbortSignal} from '@pb/kit-hooks-use-abort-signal';

import {AccountTeamMemberSuggestResult} from './account-team-meber-suggest-result';
import {SelectedTeamMember} from './selected-manager/selected-team-member';

import type {ChangeEventHandler} from 'react';
import type {AccountManagerSearchResult} from 'src/openapi/admin/models/AccountManagerSearchResult';
import type {ServerResponse} from 'src/typedef';

type Props = {
    label: string,
    selectedTeamMember?: AccountManagerSearchResult | undefined,
    onSelect: (member: AccountManagerSearchResult | undefined) => void,
    width?: number,
    placeholder?: string,
};

const MAX_RESULTS = 30;

export const AccountTeamMemberSuggest = (props: Props) => {
    const {selectedTeamMember, label, onSelect, width = 315, placeholder} = props;
    const apiDispatch = useAppDispatch();
    const {createAbortSignal, abort} = useAbortSignal();

    const [query, setQuery] = useState(selectedTeamMember?.fullName || selectedTeamMember?.email || '');
    const [busy, setBusy] = useState(false);
    const [response, setResponse] = useState<ServerResponse<Array<AccountManagerSearchResult>> | null>(null);

    useEffectWithDeps((prev, current) => {
        if (!isDeepEqual({prev, current})) {
            setQuery(selectedTeamMember?.fullName || selectedTeamMember?.email || '');
        }
    }, selectedTeamMember);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const getSuggestions = useCallback(debounce(async (newValue: string) => {
        const signal = createAbortSignal();
        const name = newValue.toLowerCase().trim();

        if (name) {
            const searchResult = await apiDispatch(runAccountManagerSearch({name}, {signal}));
            if (searchResult.payload.success) {
                setResponse({data: searchResult.payload.data.slice(0, MAX_RESULTS)});
            } else {
                setResponse({error: {
                    message: 'Can\'t suggest account team members. Service is temporary unavailable.',
                }});
            }
            setBusy(false);
        } else {
            setBusy(false);
            setResponse(null);
            abort();
        }
    }, 1000), []);

    const onChangeQuery = useCallback<ChangeEventHandler<HTMLInputElement>>(event => {
        const value = event.currentTarget.value;
        setBusy(true);
        setQuery(value);
        getSuggestions(value);
    }, [setBusy, setQuery, getSuggestions]);

    const clearSuggest = useCallback(() => {
        setQuery('');
        onSelect(undefined);
        setResponse(null);
    }, [setQuery, onSelect, setResponse]);

    const onItemSelect = useCallback((member: AccountManagerSearchResult) => {
        onSelect(member);
        setQuery('');
        setResponse(null);
    }, [onSelect, setQuery, setResponse]);

    return (
        <div style={{width}}>
            <Label htmlFor="account-team-member-autosuggest">{label}</Label>
            {selectedTeamMember ? (
                <SelectedTeamMember {...{selectedTeamMember, onSelectedClear: clearSuggest}}/>
            ) : (
                <Autosuggest
                    id="account-team-member-autosuggest"
                    value={query}
                    onChange={onChangeQuery}
                    placeholder={placeholder}
                    onItemSelect={onItemSelect}
                    onClear={clearSuggest}>
                    <AccountTeamMemberSuggestResult
                        id="account-team-member-autosuggest-list"
                        busy={busy}
                        response={response}
                    />
                </Autosuggest>
            )}
        </div>
    );
};
